import { map, isEmpty } from 'ramda';
import { combineEpics, ofType } from 'redux-observable';
import { of, concat } from 'rxjs';
import { flatMap, pluck, delay } from 'rxjs/operators';

import { actions as bootstrapActions } from '@ezugi/bootstrap';
import { GE, LAY } from '@ezugi/constants';

import { BL_BETS_ORDERS, REVERSE_BET_TYPES } from '../../../../constants/BackAndLay/betTypes';
import { myBetsActions } from '../../../actions/myBets';
import { computeMyBetsSelector } from '../../../selectors/BackAndLay';
import { numberOfCardsSelector } from '../../../selectors/cards';
import { formatBLPayout } from '../utils';

const {
  myBets,
} = myBetsActions;
const { uiActions } = bootstrapActions;

const getValue = ({ betType, payout, betAmount }) => {
  const isLayBet = REVERSE_BET_TYPES[betType].betSide === LAY;
  return isLayBet ? +(betAmount / payout).toFixed() : betAmount;
};

const getProfit = ({ betType, payout, betAmount }) => {
  const isLayBet = REVERSE_BET_TYPES[betType].betSide === LAY;
  return isLayBet ? betAmount : +(payout * betAmount).toFixed(2);
};

const getLoss = ({ betType, payout, betAmount }) => {
  const isLayBet = REVERSE_BET_TYPES[betType].betSide === LAY;
  return isLayBet ? -(betAmount * payout).toFixed(2) : -betAmount.toFixed(2);
};

const buildValueProfitLossProps = ({ betType, payout, betAmount }) => {
  const value = getValue({ betType, payout, betAmount });
  const profit = getProfit({ betType, payout, betAmount: value });
  const loss = getLoss({ betType, payout, betAmount: value });

  return { profit, value, loss };
};

const myBetsCacheEpic = (action$, state$) => action$.pipe(
  ofType(myBets.cache), // dispatched during round betting flow
  pluck('payload'),
  flatMap(() => {
    const myBetsObj = computeMyBetsSelector(state$.value);
    const myBetsWithProfits = map((obj) => ({
      ...obj,
      profit: getProfit({ betType: obj.name, payout: obj.payoutValue, betAmount: obj.value }),
      loss: getLoss({ betType: obj.name, payout: obj.payoutValue, betAmount: obj.value }),
    }), myBetsObj);
    const nbCards = numberOfCardsSelector(state$.value);
    return concat(
      of(myBets.push({ [nbCards + 1]: myBetsWithProfits })),
      of(uiActions.myBets.highlight()),
      of(uiActions.myBets.highlight(false)).pipe(delay(5000)),
    );
  })
);

const myBetsComputeEpic = (action$) => action$.pipe(
  ofType(myBets.compute), // dispatched on InitialData ws message
  pluck('payload'),
  flatMap((betsPayload = []) => {
    const myBetsObj = betsPayload.reduce((acc, { betAmount, step, type, status, approved, ...values }) => {
      acc[step + 1] = { ...acc[step + 1],
        [type]: {
          ...values,
          name: type,
          valid: approved || status === GE.IN.BET_STATUS.APPROVED,
          index: BL_BETS_ORDERS[type],
          payout: formatBLPayout(values.payout),
          ...buildValueProfitLossProps({ betType: type, payout: values.payout, betAmount }),
        } };
      return acc;
    }, {});

    const actions = [
      of(myBets.push(myBetsObj)),
      ...(isEmpty(betsPayload) ? [] : [
        of(uiActions.myBets.highlight()),
        of(uiActions.myBets.highlight(false)).pipe(delay(5000)),
      ]),
    ];
    return concat(...actions);
  })
);

export default combineEpics(
  myBetsCacheEpic,
  myBetsComputeEpic,
);
